import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { loadArticleContent } from '../data/articleContentStore'
import BlogHero from '../components/blog/BlogHero'
import BlogFeatured from '../components/blog/BlogFeatured'
import BlogGrid from '../components/blog/BlogGrid'
import BlogNewsletter from '../components/blog/BlogNewsletter'
import { articles, CATEGORY_KEYS, type Article, type CategoryKey } from '../data/articles'
import { orgSchema, breadcrumbSchema } from '../lib/seo'
import SeoHead from '../components/SeoHead'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

export default function BlogPage() {
  const { t } = useT()
  const locale = useLocale()
  const [active, setActive] = useState<CategoryKey>('all')
  const filtered: Article[] =
    active === 'all' ? articles : articles.filter((a) => a.category === active)

  // Depuis la liste, l'étape suivante est presque toujours un article : on
  // précharge le chunk pendant un temps mort, pour que la navigation client
  // affiche le corps immédiatement. Hors chemin critique — jamais sur la home.
  useEffect(() => {
    const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1200))
    const handle = idle(() => void loadArticleContent())
    return () => window.cancelIdleCallback?.(handle as number)
  }, [])

  return (
    <>
    <SeoHead
      title={t('seo.blog.title')}
      description={t('seo.blog.description')}
      path={localizedPath('blog', locale)}
      jsonLd={[
        { '@context': 'https://schema.org', ...orgSchema(locale) },
        {
          '@context': 'https://schema.org',
          ...breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.blog'), path: localizedPath('blog', locale) },
          ]),
        },
      ]}
    />
    <div className="relative">
      <BlogHero />

      <section className="relative max-w-6xl mx-auto px-6 pb-12">
        <div className="flex flex-wrap items-center gap-2">
          {CATEGORY_KEYS.map((key) => (
            <motion.button
              key={key}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setActive(key)}
              className={`relative text-sm rounded-full px-4 py-1.5 border transition-colors ${
                active === key
                  ? 'bg-accent-700 text-white border-accent-600'
                  : 'bg-white text-ink-700 border-ink-200 hover:border-accent-400 hover:text-accent-700'
              }`}
            >
              {t('blog.categories.' + key)}
            </motion.button>
          ))}
          <div className="ml-auto text-xs text-ink-500">
            {filtered.length > 1
              ? t('blog.countPlural', { count: filtered.length })
              : t('blog.countSingular', { count: filtered.length })}
          </div>
        </div>
      </section>

      <BlogFeatured article={articles[0]} />

      <BlogGrid articles={filtered.slice(1)} />

      <BlogNewsletter />
    </div>
    </>
  )
}
